/** Configuration shared by dropdowns whose options depend on other filters. */
export interface DependentDropdownConfig {
  key?: string;
  dependsOn?: string | string[];
  dependOn?: string | string[];
  scalarFilterKeys?: string[];
  [extra: string]: unknown;
}

/** Returns the normalized list of filter keys that drive a dropdown. */
export function resolveDependencyKeys(
  config: DependentDropdownConfig | undefined,
): string[] {
  const raw = config?.dependsOn ?? config?.dependOn;
  const source = Array.isArray(raw)
    ? raw
    : raw === null || raw === undefined
      ? []
      : [raw];

  return source
    .map((key) => String(key ?? '').trim())
    .filter(Boolean);
}

/**
 * Selects only declared dependencies from the global filter state. Transient
 * values such as table record lists cannot leak into a dropdown request unless
 * they are explicitly declared as dependencies.
 */
export function collectDependencyValues(
  filters: any,
  keys: string[],
  scalarKeys: string[] = [],
): Record<string, unknown> {
  const bag = {
    ...(filters?.queryParams || {}),
    ...(filters?.requestParams || {}),
  };
  const scalars = new Set(
    scalarKeys
      .map((key) => String(key ?? '').trim())
      .filter(Boolean),
  );

  const values: Record<string, unknown> = {};
  for (const key of keys) {
    const raw = bag[key];
    const list = (Array.isArray(raw) ? raw : [raw])
      .flat(Infinity)
      .filter((item) => item !== null && item !== undefined && item !== '');

    if (!list.length) continue;
    values[key] = scalars.has(key) ? String(list[0]) : list;
  }

  return values;
}

/** Stable identity used to suppress duplicate option requests. */
export function dependencySignature(values: Record<string, unknown>): string {
  const ordered: Record<string, unknown> = {};
  for (const key of Object.keys(values).sort()) {
    ordered[key] = values[key];
  }
  return JSON.stringify(ordered);
}
